import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { CierreMensualApi } from "../api/CierreMensualApi";
import {
    TIPO_BODEGA,
    TIPO_MATERIA_PRIMA,
    incluyeBodega,
    opcionesTipoRegistro,
    tiposDelRegistro,
} from "../../../../helpers/tiposCierreMensual";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";

// cada tipo envía las observaciones del pie en su propio campo
const CAMPOS_OBSERVACIONES = {
    [TIPO_MATERIA_PRIMA]: "observaciones_materia_prima",
    [TIPO_BODEGA]: "observaciones_bodega",
};

// Estado de la pantalla que registra un cierre mensual. Primero se elige el
// período y después el tipo, entre los que ese período admite: el completo, que
// registra las dos mitades del formato, o una sola. La fecha de corte la piden
// el completo y el de bodega. Al registrar, la pantalla vuelve al listado con
// onRegistrado
export const useCierreMensual = (onRegistrado) => {
    const [periodos, setPeriodos] = useState([]);
    const [periodo, setPeriodo] = useState(null);

    const [tipo, setTipo] = useState(null);

    const [fechaCorte, setFechaCorte] = useState(null);
    const [fechaMinimaCorte, setFechaMinimaCorte] = useState(null);

    const [resumen, setResumen] = useState(null);
    const [estadoPeriodo, setEstadoPeriodo] = useState(null);

    // van al pie del Excel, donde el cliente las escribía a mano. Una por tipo,
    // porque cada cierre guarda las suyas
    const [observaciones, setObservaciones] = useState({});

    // borrador de la columna de observaciones de la tabla de bodega, por
    // producto. No se guardan hasta registrar el cierre
    const [observacionesProductos, setObservacionesProductos] = useState({});

    // Cambiar de período dispara consultas encadenadas. Si una vieja responde
    // después de una nueva, pisaría la selección actual, así que cada cambio
    // invalida lo que estaba en vuelo
    const seleccionVigente = useRef(0);

    const {
        getPeriodosACerrar,
        getLimitesCorte,
        getEstadoPeriodo,
        getPrevisualizacion,
        registrarCierreMensual,
        cargando,
    } = CierreMensualApi();

    const CambiarObservaciones = (tipoCierre, texto) => {
        setObservaciones((previas) => ({
            ...previas,
            [tipoCierre]: texto,
        }));
    };

    const CambiarObservacionProducto = (productoId, texto) => {
        setObservacionesProductos((previas) => ({
            ...previas,
            [productoId]: texto,
        }));
    };

    const LimpiarConsulta = () => {
        setResumen(null);
        setEstadoPeriodo(null);
    };

    const CargarPeriodos = async () => {
        try {
            const response = await getPeriodosACerrar();

            setPeriodos(response);

            // con las cadenas al día es uno solo, así que se deja elegido
            if (response.length === 1) {
                SeleccionarPeriodo(response[0]);
            }
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener los períodos pendientes de cerrar",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    useEffect(() => {
        CargarPeriodos();
    }, []);

    // El tipo propuesto es el primero de las opciones: el completo cuando el
    // período lo admite. La fecha de corte se propone cuando el período admite
    // bodega y se conserva al cambiar de tipo
    const SeleccionarPeriodo = (nuevoPeriodo) => {
        const seleccion = ++seleccionVigente.current;

        const opciones = nuevoPeriodo
            ? opcionesTipoRegistro(nuevoPeriodo.tipos_disponibles)
            : [];

        const admiteBodega =
            !!nuevoPeriodo &&
            nuevoPeriodo.tipos_disponibles.includes(TIPO_BODEGA);

        setPeriodo(nuevoPeriodo);
        setTipo(opciones.length ? opciones[0].value : null);
        setFechaMinimaCorte(null);
        LimpiarConsulta();

        // se propone cortar en este momento, que es el caso normal: bodega
        // corta cuando deja de recibir producción de los supervisores
        setFechaCorte(admiteBodega ? moment() : null);

        if (admiteBodega) {
            CargarLimites(nuevoPeriodo, seleccion);
        }
    };

    const CambiarTipo = (nuevoTipo) => {
        setTipo(nuevoTipo);

        LimpiarConsulta();
    };

    const CambiarFechaCorte = (nuevaFecha) => {
        setFechaCorte(nuevaFecha);

        LimpiarConsulta();
    };

    const CargarLimites = async (periodoElegido, seleccion) => {
        try {
            const response = await getLimitesCorte({
                historico_id: periodoElegido.id,
            });

            if (seleccion !== seleccionVigente.current) {
                return;
            }

            const minima = response.fecha_corte_minima
                ? moment(response.fecha_corte_minima)
                : null;

            setFechaMinimaCorte(minima);

            // el corte propuesto es "ahora", que queda por debajo del mínimo
            // cuando el período todavía no termina
            if (minima) {
                setFechaCorte((actual) =>
                    actual && actual.isBefore(minima) ? minima : actual,
                );
            }
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener la fecha mínima de corte",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const ParametrosDeConsulta = () => {
        const parametros = { tipo: tipo, historico_id: periodo.id };

        if (incluyeBodega(tipo)) {
            parametros.fecha_corte = moment(fechaCorte).format();
        }

        return parametros;
    };

    // El estado se pide primero: cuando el período no se puede cerrar, es el que
    // explica por qué, y la previsualización en ese caso solo daría un error sin
    // el motivo
    const Consultar = async () => {
        LimpiarConsulta();

        const parametros = ParametrosDeConsulta();

        try {
            const estado = await getEstadoPeriodo(parametros);

            setEstadoPeriodo(estado);

            if (!estado.puede_cerrar) {
                return;
            }

            setResumen(await getPrevisualizacion(parametros));
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al consultar el período",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const RegistrarCierre = async () => {
        const datos = ParametrosDeConsulta();

        tiposDelRegistro(tipo).forEach((tipoCierre) => {
            datos[CAMPOS_OBSERVACIONES[tipoCierre]] =
                observaciones[tipoCierre] || "";
        });

        // la columna de observaciones por producto es de la tabla de bodega
        if (incluyeBodega(tipo)) {
            // las llaves del objeto son texto, el backend espera el id
            datos.observaciones_productos = Object.keys(
                observacionesProductos,
            ).map((productoId) => ({
                producto_id: Number(productoId),
                observaciones: observacionesProductos[productoId],
            }));
        }

        try {
            await registrarCierreMensual(datos);

            NotificationManager.success(
                "Cierre mensual registrado",
                "Éxito",
                3000,
            );

            onRegistrado();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al registrar el cierre mensual",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    // react-datepicker aplica los límites de hora a todos los días por igual,
    // así que la hora solo se restringe cuando el día elegido es el del mínimo;
    // en los días posteriores la jornada entera es válida
    const HorasCorte = () => {
        if (!fechaCorte) {
            return { minima: null, maxima: null };
        }

        const esDiaDelMinimo =
            fechaMinimaCorte && fechaCorte.isSame(fechaMinimaCorte, "day");

        return {
            minima: esDiaDelMinimo
                ? fechaMinimaCorte
                : moment(fechaCorte).startOf("day"),
            maxima: moment(fechaCorte).endOf("day"),
        };
    };

    const conBodega = incluyeBodega(tipo);

    return {
        periodos,
        periodo,
        tipo,
        opcionesTipo: periodo
            ? opcionesTipoRegistro(periodo.tipos_disponibles)
            : [],
        tiposIncluidos: tipo ? tiposDelRegistro(tipo) : [],
        incluyeBodega: conBodega,
        fechaCorte,
        fechaMinimaCorte,
        horasCorte: HorasCorte(),
        resumen,
        estadoPeriodo,
        observaciones,
        observacionesProductos,

        puedeConsultar: !!periodo && !!tipo && (!conBodega || !!fechaCorte),
        puedeCerrar: !!(estadoPeriodo && estadoPeriodo.puede_cerrar),

        CambiarObservaciones,
        CambiarObservacionProducto,
        CambiarTipo,
        SeleccionarPeriodo,
        CambiarFechaCorte,
        Consultar,
        RegistrarCierre,
        cargando,
    };
};
